import axios from 'axios';
import { isFeatureEnabled } from '../config/features';

const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:3001') + '/api';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 second timeout for AI responses
  withCredentials: true, // Include cookies for authentication
});

/**
 * AI Agent Service - Frontend interface for chat functionality
 */
export class AIAgentService {
  /**
   * Send a message to the AI agent
   * @param {string} message - User message
   * @param {string} conversationId - Optional conversation ID
   * @returns {Promise<Object>} AI response
   */
  static async sendMessage(message, conversationId = null) {
    // Return error if AI features are disabled
    if (!isFeatureEnabled('AI_AGENT_SERVICE')) {
      return {
        success: false,
        error: 'AI chat functionality is currently disabled.',
        status: 503
      };
    }

    try {
      const response = await apiClient.post('/ai-agent/chat', {
        message: message.trim(),
        conversationId
      });

      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('AI Agent error:', error);
      
      const errorMessage = error.response?.data?.error || 
                          error.message || 
                          'Failed to send message. Please try again.';
      
      return {
        success: false,
        error: errorMessage,
        status: error.response?.status
      };
    }
  }

  /**
   * Get conversation history
   * @param {string} sessionId - Conversation session ID
   * @returns {Promise<Object>} Conversation history
   */
  static async getConversationHistory(sessionId) {
    try {
      const response = await apiClient.get(`/ai-agent/conversations/${sessionId}`);
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error fetching conversation history:', error);
      
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to load conversation history'
      };
    }
  }

  /**
   * Get user's conversations list
   * @returns {Promise<Object>} List of conversations
   */
  static async getUserConversations() {
    try {
      const response = await apiClient.get('/ai-agent/conversations');
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error fetching conversations:', error);
      
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to load conversations'
      };
    }
  }

  /**
   * Clear conversation history
   * @param {string} sessionId - Conversation session ID
   * @returns {Promise<Object>} Success status
   */
  static async clearConversation(sessionId) {
    try {
      const response = await apiClient.delete(`/ai-agent/conversations/${sessionId}`);
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error clearing conversation:', error);
      
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to clear conversation'
      };
    }
  }

  /**
   * Submit user feedback for a conversation
   * @param {string} conversationId - Conversation ID
   * @param {number} rating - Rating (1-5)
   * @param {string} feedback - Optional text feedback
   * @returns {Promise<Object>} Success status
   */
  static async submitFeedback(conversationId, rating, feedback = '') {
    try {
      const response = await apiClient.post('/ai-agent/feedback', {
        conversationId,
        rating,
        feedback: feedback.trim()
      });
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error submitting feedback:', error);
      
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to submit feedback'
      };
    }
  }

  /**
   * Generate a unique conversation ID
   * @returns {string} Unique conversation ID
   */
  static generateConversationId() {
    return `conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Check if user is authenticated
   * @returns {Promise<Object>} Authentication status
   */
  static async checkAuthStatus() {
    try {
      // Try to make a simple request to check auth
      const response = await apiClient.get('/ai-agent/conversations');
      
      return {
        isAuthenticated: true,
        data: response.data
      };
    } catch (error) {
      console.error('Auth check error:', error);
      
      if (error.response?.status === 401) {
        return {
          isAuthenticated: false,
          error: 'Please log in to use the chat feature'
        };
      }
      
      if (error.response?.status === 404) {
        return {
          isAuthenticated: false,
          error: 'Chat service not available. Please try again.'
        };
      }
      
      return {
        isAuthenticated: false,
        error: 'Unable to verify authentication status'
      };
    }
  }
}

export default AIAgentService; 